import { supabase } from '../utils/supabase';
import { Consulta, TinyPacient } from '../types';

export interface ConsultasListResponse {
  success: boolean;
  message: string;
  data?: Consulta[];
  count?: number;
  error?: Error;
}

export interface RecentConsultasListResponse {
  success: boolean;
  message: string;
  data?: RecentConsulta[];
  count?: number;
  error?: Error;
}

export interface ConsultaResponse {
  success: boolean;
  message: string;
  data?: Consulta;
  error?: Error;
}

export interface ConsultasStatsResponse {
  success: boolean;
  message: string;
  data?: {
    totalConsultas: number;
    consultasHoje: number;
    pacientesUnicos: number;
  };
  error?: Error;
}

export interface ConsultaDeleteResponse {
  success: boolean;
  message: string;
  error?: Error;
}

export interface ConsultasCountResponse {
  success: boolean;
  message: string;
  count?: number;
  error?: Error;
}

export interface RecentConsulta {
  id: string;
  created_at: string;
  queixa: string;
  paciente: TinyPacient;
  pacientes: TinyPacient[];
}

export class ConsultasService {
  /**
   * Get paginated list of consultas for an organization
   */
  static async getConsultas(
    organizationId: string,
    page: number = 0,
    limit: number = 10,
    query?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ConsultasListResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      let supabaseQuery = supabase
        .from('consultas')
        .select(
          `*,
           paciente:pacientes!inner (
             id,
             nome,
             data_nasc
           )`,
          { count: 'exact' }
        )
        .eq('organization_id', organizationId);

      if (query) {
        supabaseQuery = supabaseQuery.ilike('pacientes.nome', `%${query}%`);
      }

      if (startDate) {
        supabaseQuery = supabaseQuery.gte('created_at', startDate + 'T00:00:00');
      }

      if (endDate) {
        supabaseQuery = supabaseQuery.lte('created_at', endDate + 'T23:59:59');
      }

      supabaseQuery = supabaseQuery
        .range(page * limit, (page + 1) * limit - 1)
        .order('created_at', { ascending: false });

      const { data, count, error } = await supabaseQuery;

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Consultas carregadas com sucesso',
        data: (data as Consulta[]) || [],
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get consultas statistics for an organization
   */
  static async getConsultasStats(
    organizationId: string,
    query?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ConsultasStatsResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      // Get total consultas count (with filter if searching)
      let totalQuery = supabase
        .from('consultas')
        .select('*, pacientes!inner(id, nome)', { count: 'exact', head: true })
        .eq('organization_id', organizationId);

      if (query) {
        totalQuery = totalQuery.ilike('pacientes.nome', `%${query}%`);
      }

      if (startDate) {
        totalQuery = totalQuery.gte('created_at', startDate + 'T00:00:00');
      }

      if (endDate) {
        totalQuery = totalQuery.lte('created_at', endDate + 'T23:59:59');
      }

      const { count: totalCount, error: totalError } = await totalQuery;
      if (totalError) throw totalError;

      // Get consultas hoje (today) count (with filter if searching)
      const today = new Date();
      const startOfDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      ).toISOString();
      const endOfDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 1
      ).toISOString();

      let hojeQuery = supabase
        .from('consultas')
        .select('*, pacientes!inner(id, nome)', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .gte('created_at', startOfDay)
        .lt('created_at', endOfDay);

      if (query) {
        hojeQuery = hojeQuery.ilike('pacientes.nome', `%${query}%`);
      }

      if (startDate && startDate <= today.toISOString().split('T')[0]) {
        const filterStartDate =
          startDate > today.toISOString().split('T')[0]
            ? startDate
            : today.toISOString().split('T')[0];
        hojeQuery = hojeQuery.gte('created_at', filterStartDate + 'T00:00:00');
      }

      if (endDate && endDate >= today.toISOString().split('T')[0]) {
        const filterEndDate =
          endDate < today.toISOString().split('T')[0] ? endDate : today.toISOString().split('T')[0];
        hojeQuery = hojeQuery.lte('created_at', filterEndDate + 'T23:59:59');
      }

      const { count: hojeCount, error: hojeError } = await hojeQuery;
      if (hojeError) throw hojeError;

      // Get unique pacientes count (with filter if searching)
      let uniquePacientesQuery = supabase
        .from('consultas')
        .select('pacientes!inner(id, nome)')
        .eq('organization_id', organizationId);

      if (query) {
        uniquePacientesQuery = uniquePacientesQuery.ilike('pacientes.nome', `%${query}%`);
      }

      if (startDate) {
        uniquePacientesQuery = uniquePacientesQuery.gte('created_at', startDate + 'T00:00:00');
      }

      if (endDate) {
        uniquePacientesQuery = uniquePacientesQuery.lte('created_at', endDate + 'T23:59:59');
      }

      const { data: pacientesData, error: pacientesError } = await uniquePacientesQuery;
      if (pacientesError) throw pacientesError;

      // Count unique pacientes
      const uniquePacienteIds = new Set(
        pacientesData?.map((c: Record<string, any>) => c.pacientes?.id).filter(Boolean) || []
      );

      return {
        success: true,
        message: 'Estatísticas de consultas carregadas com sucesso',
        data: {
          totalConsultas: totalCount || 0,
          consultasHoje: hojeCount || 0,
          pacientesUnicos: uniquePacienteIds.size
        }
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get a specific consulta by ID
   */
  static async getConsultaById(
    consultaId: string,
    organizationId: string
  ): Promise<ConsultaResponse> {
    if (!consultaId) {
      return {
        success: false,
        message: 'Consulta ID is required',
        error: new Error('Consulta ID is required')
      };
    }

    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { data, error } = await supabase
        .from('consultas')
        .select('*, paciente:pacientes(id, nome, data_nasc)')
        .eq('id', consultaId)
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Consulta encontrada com sucesso',
        data: data as Consulta
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Create or update a consulta
   */
  static async upsertConsulta(
    organizationId: string,
    consultaData: Partial<Consulta>
  ): Promise<ConsultaResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const bodyWithOrg = {
        ...consultaData,
        organization_id: organizationId
      };

      // Clean up fields that shouldn't be in upsert
      if (!bodyWithOrg.id) delete bodyWithOrg.id;
      delete bodyWithOrg.created_at;
      delete bodyWithOrg.updated_at;

      const { data, error } = await supabase
        .from('consultas')
        .upsert(bodyWithOrg)
        .select('*, paciente:pacientes(id, nome, data_nasc)')
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: consultaData.id
          ? 'Consulta atualizada com sucesso'
          : 'Consulta criada com sucesso',
        data: data as Consulta
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Delete a consulta by ID
   */
  static async deleteConsulta(
    consultaId: string,
    organizationId: string
  ): Promise<ConsultaDeleteResponse> {
    if (!consultaId) {
      return {
        success: false,
        message: 'Consulta ID is required',
        error: new Error('Consulta ID is required')
      };
    }

    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { error } = await supabase
        .from('consultas')
        .delete()
        .eq('id', consultaId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Consulta removida com sucesso'
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get consultas count for dashboard stats
   */
  static async getConsultasCount(organizationId: string): Promise<ConsultasCountResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { count, error } = await supabase
        .from('consultas')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Contagem de consultas realizada com sucesso',
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get consultas count for today
   */
  static async getConsultasTodayCount(organizationId: string): Promise<ConsultasCountResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const today = new Date().toISOString().split('T')[0];

      const { count, error } = await supabase
        .from('consultas')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .gte('created_at', today + 'T00:00:00')
        .lt('created_at', today + 'T23:59:59');

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Contagem de consultas de hoje realizada com sucesso',
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get consultas count for this month
   */
  static async getConsultasThisMonthCount(organizationId: string): Promise<ConsultasCountResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const today = new Date();
      const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString();
      const firstDayNextMonth = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        1
      ).toISOString();

      const { count, error } = await supabase
        .from('consultas')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .gte('created_at', firstDayThisMonth)
        .lt('created_at', firstDayNextMonth);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Contagem de consultas do mês realizada com sucesso',
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get recent consultas for dashboard
   */
  static async getRecentConsultas(
    organizationId: string,
    limit: number = 5
  ): Promise<RecentConsultasListResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { data, error } = await supabase
        .from('consultas')
        .select(
          `
          *,
          paciente:pacientes!inner (
            id,
            nome,
            data_nasc
          )
        `
        )
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Consultas recentes carregadas com sucesso',
        data: (data as RecentConsulta[]) || []
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get consultas count for filtered pacientes (used in usePacientes hook)
   */
  static async getConsultasCountForPacientes(
    organizationId: string,
    nome?: string
  ): Promise<ConsultasCountResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      let query = supabase
        .from('consultas')
        .select('*, pacientes!inner(id, nome)', { count: 'exact', head: true })
        .eq('organization_id', organizationId);

      if (nome !== undefined) {
        query = query.ilike('pacientes.nome', `%${nome}%`);
      }

      const { count, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Contagem de consultas para pacientes realizada com sucesso',
        count: count || 0
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }
}
